import { ICommon, IUser } from 'domain/env/types';
import { AppState } from 'domain/reducers';
import { ANALYTICS_EVENTS, setUserProperties, trackEvent } from 'libs/analytics';
import { getIn } from 'formik';
import { TAllPermissions } from 'hooks/usePermission';
import api from 'libs/api';
import {
  ACCOUNT_DASHBOARD_PERMISSIONS,
  ACCOUNT_GLOBAL_SETTINGS_PERMISSIONS,
  COMMON_TYPE_ACCOUNT,
  COOKIES_LIFETIME,
  DEFAULT_TIMEZONE,
  HTTP_STATUSES,
  RESET_PASSWORD_KEYS,
  RESET_PASSWORD_REDIRECT_PATH,
  STRONG_ACCOUNT_ADMIN_PAGES_PERMISSIONS,
  STRONG_COMPANY_ADMIN_PAGES_PERMISSIONS,
  TOKEN_KEYS,
} from 'libs/constants';
import cookies from 'libs/cookie';
import { clearRequiredFormsStorage } from 'libs/requiredFormsStorage';
import getSubdomain from 'libs/subdomain';
import { createPermission, createStrongPermission, fullUrlPath, getTokens } from 'libs/utils';
import { IResetFormValues } from 'pages/Auth/UserSignIn/Forgot';
import { IResetPasswordFormValues } from 'pages/Auth/UserSignIn/Reset';
import { ISignInFormValues } from 'pages/Auth/UserSignIn/SignIn';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import {
  commonActions,
  doneRedirectAfterSignInAction,
  logOutAction,
  needRedirectAfterSignInAction,
  resetPasswordActions,
  setUserAction,
  signInActions,
} from './actions';
import {
  envIsAuthorizedSelector,
  envIsLoadingSelector,
  envNeedRedirectAfterSignInSelector,
  envUserSelector,
} from './selectors';
import { NavigateFunction } from 'react-router-dom';
import { AxiosResponse, Cancel } from 'axios';

interface IHeaders {
  [key: string]: string;
}

const getHeaderValue =
  (headers: IHeaders) =>
  (key: string): string =>
    headers[key];
const getSearchParamValue =
  (headers: URLSearchParams) =>
  (key: string): string | null =>
    headers.get(key);

const resolveLoginFailureReason = (error: unknown): string => {
  const status = getIn(error, 'response.status');
  if (!status) return 'network_error';
  if (status === HTTP_STATUSES.unauthorized || status === HTTP_STATUSES.unprocessableEntity) {
    return 'invalid_credentials';
  }
  if (status >= HTTP_STATUSES.internalServerError) return 'server_error';
  return 'other';
};

const resolveUserRole = (common: ICommon): string => common.profile?.primary_group_key || 'unknown';

// the avatar link is signed and expires, so it is kept in the store only
const getCookieUser = ({ avatar, ...user }: IUser) => user;

export const thunkSignIn =
  (
    data: ISignInFormValues,
    navigate: NavigateFunction
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch) => {
    try {
      dispatch(signInActions.request());
      const URLParams = new URLSearchParams(window.location.search);
      const redirectTo = URLParams.get('redirect_to');
      const response = await api.auth.signIn({ data });
      const tokens = getTokens(TOKEN_KEYS, getHeaderValue(response.headers));
      const user = response.data.resource;
      const auth = { user, tokens };

      cookies.set('auth', JSON.stringify({ ...auth, user: getCookieUser(user) }), {
        domain: process.env.REACT_APP_FRONTEND_DOMAIN,
        expires: new Date(Date.now() + COOKIES_LIFETIME),
        path: '/',
      });

      if (!redirectTo) {
        // Must dispatch before success sign in action
        dispatch(needRedirectAfterSignInAction());
      }
      dispatch(signInActions.success(auth));

      trackEvent(ANALYTICS_EVENTS.LOGIN, { method: 'email' });

      if (redirectTo) {
        const regexp = new RegExp('https?://');
        if (regexp.test(redirectTo)) {
          window.location.replace(redirectTo);
        } else {
          navigate(redirectTo);
        }
      } else {
        navigate('/');
      }
    } catch (e) {
      trackEvent(ANALYTICS_EVENTS.LOGIN_FAILURE, {
        method: 'email',
        failure_reason: resolveLoginFailureReason(e),
      });
      //@ts-ignore
      dispatch(signInActions.failure(e));
    }
  };

export const thunkForgotPassword =
  (
    data: IResetFormValues,
    navigate: NavigateFunction,
    handleHttpError: (error: { response: AxiosResponse } | Cancel | unknown) => void
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch) => {
    try {
      const { subdomain } = getSubdomain();
      dispatch(resetPasswordActions.request());
      const response = await api.auth.forgotPassword({
        data: {
          ...data,
          redirect_url: subdomain
            ? fullUrlPath(subdomain, '/' + RESET_PASSWORD_REDIRECT_PATH)
            : process.env.REACT_APP_URL + RESET_PASSWORD_REDIRECT_PATH,
        },
      });
      dispatch(resetPasswordActions.success({ user: response.data.resource }));
      trackEvent(ANALYTICS_EVENTS.FORGOT_PASSWORD, { method: 'email' });
      navigate('/forgot-sent');
    } catch (e) {
      //@ts-ignore
      dispatch(resetPasswordActions.failure(e));
      handleHttpError(e);
    }
  };

export const thunkResetPassword =
  (
    data: IResetPasswordFormValues,
    searchParams: URLSearchParams,
    navigate: NavigateFunction,
    handleHttpError: (error: { response: AxiosResponse } | Cancel | unknown) => void
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch) => {
    try {
      const headers = getTokens(RESET_PASSWORD_KEYS, getSearchParamValue(searchParams));
      dispatch(resetPasswordActions.request());
      const response = await api.auth.resetPassword({
        data: { ...data, reset_password_token: searchParams.get('reset_password_token') },
        headers,
      });
      dispatch(resetPasswordActions.success({ user: response.data.resource }));
      trackEvent(ANALYTICS_EVENTS.RESET_PASSWORD, { method: 'email', outcome: 'success' });
      const URLParams = new URLSearchParams(window.location.search);
      const redirectTo = URLParams.get('redirect_to');
      if (redirectTo) {
        navigate(`/sign-in?redirect_to=${encodeURIComponent(redirectTo)}`);
      } else {
        navigate('/sign-in');
      }
    } catch (e) {
      //@ts-ignore
      dispatch(resetPasswordActions.failure(e));
      handleHttpError(e);
    }
  };

export const updateUser =
  (newUserInfo: IUser): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    const auth = cookies.get('auth') || { tokens: {}, user: {} };
    const user: IUser = {
      ...envUserSelector(getState()),
      ...newUserInfo,
    };
    cookies.set(
      'auth',
      JSON.stringify({
        ...auth,
        user: getCookieUser(user),
      }),
      {
        domain: process.env.REACT_APP_FRONTEND_DOMAIN,
        expires: new Date(Date.now() + COOKIES_LIFETIME),
        path: '/',
      }
    );
    dispatch(
      setUserAction({
        user,
      })
    );
  };

export const thunkLogOut =
  (navigate?: NavigateFunction): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    const state = getState();
    const isAuthorized = envIsAuthorizedSelector(state);
    if (isAuthorized) {
      trackEvent(ANALYTICS_EVENTS.LOGOUT);
      dispatch(logOutAction());
      clearRequiredFormsStorage();
      await cookies.remove('auth', {
        domain: process.env.REACT_APP_FRONTEND_DOMAIN,
        path: '/',
      });
      navigate ? navigate('/sign-in') : (window.location.href = '/sign-in');
    }
  };

export const thunkCommon =
  (
    navigate: NavigateFunction,
    handleHttpError: (
      error: { response: AxiosResponse } | Cancel | unknown,
      field: string | undefined,
      useNotFound: boolean
    ) => void,
    options?: { silent?: boolean }
  ): ThunkAction<void, AppState, null, Action<string>> =>
  async (dispatch, getState) => {
    const state = getState();
    const isProfileLoading = envIsLoadingSelector(state);
    const needRedirectAfterSignIn = envNeedRedirectAfterSignInSelector(state);
    const silent = options?.silent ?? false;
    if (!silent && isProfileLoading) {
      return;
    }
    try {
      if (!silent) {
        dispatch(commonActions.request());
      }
      const response = await api.common();
      const common = response.data.resource;

      if (common.type) {
        const { subdomain } = getSubdomain();

        cookies.set('timezone', getIn(common, ['account', 'time_zone'], DEFAULT_TIMEZONE), {
          domain: `${subdomain}${process.env.REACT_APP_FRONTEND_DOMAIN}`,
          expires: new Date(Date.now() + COOKIES_LIFETIME),
          path: '/',
        });

        if (common.profile && common.profile.discarded_at === null) {
          let permissions;

          if (common.type === COMMON_TYPE_ACCOUNT) {
            permissions = {
              dashboard: createPermission(
                common.profile.permissions,
                ACCOUNT_DASHBOARD_PERMISSIONS as TAllPermissions[]
              ),
              global_settings: createPermission(
                common.profile.permissions,
                ACCOUNT_GLOBAL_SETTINGS_PERMISSIONS as TAllPermissions[]
              ),
            };
          } else {
            permissions = {
              dashboard: ['read'],
            };
          }

          common.profile.permissions = {
            ...common.profile.permissions,
            ...permissions,
          };

          // Permission "administration" must be added last
          common.profile.permissions.administration =
            common.type === COMMON_TYPE_ACCOUNT
              ? createStrongPermission(
                  common.profile.permissions,
                  STRONG_ACCOUNT_ADMIN_PAGES_PERMISSIONS
                )
              : createStrongPermission(
                  common.profile.permissions,
                  STRONG_COMPANY_ADMIN_PAGES_PERMISSIONS
                );
        } else {
          common.profile = {};
        }
      }
      dispatch(commonActions.success({ common }));

      if (!silent && common.type) {
        const typedCommon = common as ICommon;
        setUserProperties({
          account_id: typedCommon.account?.id ?? typedCommon.company?.id,
          account_subdomain:
            typedCommon.account?.subdomain ??
            typedCommon.company?.subdomain ??
            typedCommon.main?.subdomain,
          user_role: resolveUserRole(typedCommon),
          plan: typedCommon.account?.plan?.title,
        });
      }

      if (needRedirectAfterSignIn) {
        dispatch(doneRedirectAfterSignInAction());
        navigate(common.profile?.id ? '/dashboard' : '/');
      }
    } catch (e) {
      if (!silent) {
        //@ts-ignore
        dispatch(commonActions.failure(e));
      }
      handleHttpError(e, undefined, true);
    }
  };
